// MACHINERY V2 (Stroje) — grouped 3-up card grid (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2. Reference impl:
// design_handoff_machinery_v3/machinery-v1-big.jsx (V1Body, GroupHeader).
//
// SECTIONS ARRIVE PRE-GROUPED from useFleetPageData, by the slice's
// `fleetGroupBy` (M2.3 ruling (i)) — the SAME map Fleet V2 renders as rows, so
// the two views can never section the same fleet differently. The per-group
// collapse mirrors the live grid's, and is keyed on the RAW group key so a
// language switch cannot silently re-expand a section.
//
// DESKTOP IS 3-UP (the sub-phase baseline). Tablet/phone tiers are M2.11, but
// this must not HARD-BREAK before then: the columns step 3 -> 2 -> 1 off the
// module's own below-desktop query (`useV2MediaQuery(V2_BELOW_DESKTOP)`, never
// MUI useMediaQuery), and every card is `minWidth: 0` inside a
// `minmax(0, 1fr)` track so nothing overflows at an intermediate width.
//
// ============================================================
//  M2.16b — THE BELOW-1280 TIER, AND WHY IT NEEDS CSS
// ============================================================
// The design package steps 1 / 2 / 3 columns at 390 / 768 / 1024
// (mobile/screenshots 05, 07 and 08, all three viewed before this edit), i.e. it
// splits the TABLET tier at 1024 — a step no JS tier expresses, so below 1280
// the track list lives in `.mv2-fleet-grid` in mobile/machinery-v2-mobile.scss —
// the precedent this module already set for a within-tier distinction. The flat
// `isTablet ? 2` step above therefore became 2-then-3; that is the "verify it
// matches these exact steps" half of the task, and it did not.
//
// >>> DESKTOP IS UNTOUCHED, WITH ONE AUTHORISED EXCEPTION. <<< At >= 1280 the
// wrapper carries NO className and the SAME inline track list, gap and margin it
// always had, and the card is still FleetCard. The exception is the GroupHeader's
// left accent rule, which review finding F3 found had never painted at any width
// because a `border: 0` written after it reset it; fixing the ordering restores
// the DESIGNED idiom and makes this header render identically to the one V2 and
// V3 already use. It is a one-line reorder, authorised for this finding alone —
// not a licence for other desktop changes.
//
// BELOW 1280 THE CARD ITSELF CHANGES, not just the column count: boards 07 and
// 08 draw the COMPACT card two- and three-up, not the desktop card reflowed.
// See FleetCardCompact.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import { Machine } from "../../models/models";
import FleetCard from "./FleetCard";
import FleetCardCompact from "./FleetCardCompact";
import { FleetMenuAction } from "./FleetCardMenu";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import { MachineryV2Telemetry, unconnectedTelemetry } from "./cardStates";
import { FleetGroups } from "./fleetSelectors";
import { sectionTitle } from "./fleetGroupBy";

const GroupHeader: React.FC<{
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  /** M2.16b: the >= 44px BOTH-axes law below 1280. `12px 14px` around a 17px
   *  line box is ~41px, so this collapse control — the only one V1 has — was
   *  3px short on every phone and tablet. `undefined` at desktop emits no
   *  declaration at all, so the >= 1280 DOM is unchanged. */
  minHeight?: number;
}> = ({ title, count, collapsed, onToggle, minHeight }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={!collapsed}
    style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      // >>> ORDER IS LOAD-BEARING (review finding F3). <<< React assigns inline
      // style keys in object order, so the `border: 0` shorthand RESETS every
      // longhand written before it. These two used to be the other way round,
      // which meant this header's module-accent left rule was wiped and never
      // painted at any width — while FleetGroupHeader.tsx:92, writing the same
      // pair in this order, painted its 3px rule correctly. The fix is the
      // ordering, not a new declaration: `border: 0` first to clear the button's
      // UA border, then the one edge we want.
      padding: "12px 14px", border: 0,
      borderLeft: "2px solid var(--mod-accent)",
      borderRadius: 8, background: "var(--mod-accent-soft)",
      cursor: "pointer", fontFamily: "inherit", textAlign: "left", minHeight,
    }}
  >
    <span style={{ color: "var(--mod-accent)", fontSize: 11 }}>
      {collapsed ? "▸" : "▾"}
    </span>
    <span style={{ fontWeight: 600, color: "var(--ink)", flex: 1, minWidth: 0 }}>
      {title}
    </span>
    <span
      style={{
        fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--mod-accent)",
        background: "var(--card)", padding: "2px 8px", borderRadius: 999,
        border: "1px solid var(--mod-accent-line)",
      }}
    >
      {count}
    </span>
  </button>
);

export interface FleetGridProps {
  groups: FleetGroups;
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  newMachineId: string | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (name: string) => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetGrid: React.FC<FleetGridProps> = ({
  groups, telemetry, readiness, newMachineId, collapsedGroups, onToggleGroup,
  buildActions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  // F-A: below 1280 is read as the media QUERY both v2 stylesheets fence on,
  // never as an integer width. Identical at every integer width; correct in
  // the fractional band too. See useV2MediaQuery.ts.
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
      {Object.entries(groups).map(([key, list]) => {
        const collapsed = collapsedGroups.has(key);
        return (
          <div key={key} style={{ minWidth: 0 }}>
            <GroupHeader
              // Collapse keys on the RAW group key; only the label translates.
              title={sectionTitle(key, translate)}
              count={list.length}
              collapsed={collapsed}
              onToggle={() => onToggleGroup(key)}
              minHeight={isDesktop ? undefined : 44}
            />
            {!collapsed && (
              <div
                className={isDesktop ? undefined : "mv2-fleet-grid"}
                style={
                  isDesktop
                    ? {
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 16,
                        marginTop: 14,
                        minWidth: 0,
                      }
                    : { marginTop: 12, minWidth: 0 }
                }
              >
                {list.map((m) =>
                  isDesktop ? (
                    <FleetCard
                      key={m.id}
                      machine={m}
                      telemetry={telemetry.get(m.id) || unconnectedTelemetry()}
                      readiness={readiness.get(m.id) || null}
                      isNew={m.id === newMachineId}
                      actions={buildActions(m)}
                      onOpenProfile={() => onOpenProfile(m)}
                      onOpenReadiness={() => onOpenReadiness(m)}
                    />
                  ) : (
                    // No `readiness` / `onOpenReadiness`: the compact card
                    // carries no capability badges — see its header for why the
                    // 44px law rules a 14px overlay button out at this width.
                    <FleetCardCompact
                      key={m.id}
                      machine={m}
                      telemetry={telemetry.get(m.id) || unconnectedTelemetry()}
                      isNew={m.id === newMachineId}
                      actions={buildActions(m)}
                      onOpenProfile={() => onOpenProfile(m)}
                    />
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FleetGrid;
